import { useState, useEffect } from 'react';

/**
 * Hook to manage and access ZenEditor settings
 * Syncs with localStorage and provides real-time updates
 */
export function useEditorSettings() {
  const getDefaultSettings = () => ({
    // AI Settings
    aiModel: "llama3.1",
    aiTemperature: 0.7,
    aiMaxTokens: 500,
    aiApiUrl: process.env.REACT_APP_OLLAMA_API_URL || "http://localhost:11434",

    // Editor Settings
    fontSize: 12,
    theme: "light",
    autoSave: true,
    autoSaveInterval: 30, // seconds

    // Preview Settings
    livePreview: true,
    previewPosition: "right",
    syncScrolling: false,

    // Advanced
    showLineNumbers: false,
    wordWrap: true,
    spellCheck: true,
  });

  const loadSettings = () => {
    try {
      const saved = localStorage.getItem("zeneditor_settings");
      if (saved) {
        return { ...getDefaultSettings(), ...JSON.parse(saved) };
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
    return getDefaultSettings();
  };

  const [settings, setSettings] = useState(loadSettings());

  // Listen for storage events (settings changed in another tab/component)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "zeneditor_settings") {
        setSettings(loadSettings());
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom event (for same-tab updates)
    const handleSettingsUpdate = () => {
      setSettings(loadSettings());
    };

    window.addEventListener('zeneditor:settings-updated', handleSettingsUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('zeneditor:settings-updated', handleSettingsUpdate);
    };
  }, []);

  const updateSettings = (newSettings) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    localStorage.setItem("zeneditor_settings", JSON.stringify(updated));

    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('zeneditor:settings-updated'));
  };

  return {
    settings,
    updateSettings,
    resetSettings: () => {
      localStorage.removeItem("zeneditor_settings");
      setSettings(getDefaultSettings());
      window.dispatchEvent(new Event('zeneditor:settings-updated'));
    }
  };
}

export default useEditorSettings;
